import languagesApi from '../api/languages';
import store from '../store';
import {
  NAMESPACE as LANGUAGES_NAMESPACE,
  SET_FALLBACK_LOCALE,
  SET_LANGUAGES,
  SET_LOAD_ERROR,
  SET_LOADING,
  SET_SELECTED_LOCALE,
} from '../store/modules/languages';
import { I18nManager } from './i18nManager';

/**
 * Service to manage the available interface languages
 */
export class LanguagesManagerService {
  /**
   * @returns {string} the current selected locale
   */
  static getSelectedLocale() {
    return store.getters[`${LANGUAGES_NAMESPACE}/selectedLocale`];
  }

  /**
   * Sets the current locale
   *
   * @param {string} locale - the new locale
   * @returns {void}
   */
  static _setSelectedLocale(locale) {
    store.commit(`${LANGUAGES_NAMESPACE}/${SET_SELECTED_LOCALE}`, locale);
    I18nManager.setLocale(locale);
  }

  /**
   * Sets the current locale and changes the fallback locale
   * to the next language (if exists)
   *
   * @param {string} locale - the new locale
   * @returns {void}
   */
  static setSelectedLocale(locale) {
    this._setSelectedLocale(locale);
    const languages = LanguagesManagerService.getLanguages();
    const fallbackLanguage =
      languages.find(language => language.identifier !== locale) || languages[0];
    if (fallbackLanguage) {
      LanguagesManagerService._setFallbackLocale(fallbackLanguage.identifier);
    }
  }
  /**
   * @returns {string} the fallback locale
   */
  static getFallbackLocale() {
    return store.getters[`${LANGUAGES_NAMESPACE}/fallbackLocale`];
  }

  /**
   * Sets the fallback locale
   *
   * @param {string} locale - the new locale
   * @returns {void}
   * @private
   */
  static _setFallbackLocale(locale) {
    store.commit(`${LANGUAGES_NAMESPACE}/${SET_FALLBACK_LOCALE}`, locale);
    I18nManager.setFallbackLocale(locale);
  }

  /**
   * @returns {object[]} the language objects
   */
  static getLanguages() {
    return store.getters[`${LANGUAGES_NAMESPACE}/languages`];
  }

  /**
   * @param {object[]} languages - the language objects
   * @returns {void}
   * @private
   */
  static _setLanguages(languages) {
    store.commit(`${LANGUAGES_NAMESPACE}/${SET_LANGUAGES}`, languages);
  }

  /**
   * @returns {boolean} if loading the app languages
   */
  static getLoadingLanguages() {
    return store.getters[`${LANGUAGES_NAMESPACE}/loadingLanguages`];
  }

  /**
   * @param {boolean} loading - if loading the app languages
   * @returns {void}
   * @private
   */
  static _setLoadingLanguages(loading) {
    store.commit(`${LANGUAGES_NAMESPACE}/${SET_LOADING}`, loading);
  }

  /**
   * @returns {boolean} if an error occured while loading
   * the languages or the messages
   */
  static getLoadError() {
    return store.getters[`${LANGUAGES_NAMESPACE}/loadLanguagesError`];
  }

  /**
   * @param {boolean} hasLoadError - if an error occured while loading
   * the languages or the messages
   * @returns {void}
   * @private
   */
  static _setLoadError(hasLoadError) {
    store.commit(`${LANGUAGES_NAMESPACE}/${SET_LOAD_ERROR}`, hasLoadError);
  }

  /**
   * Retrieves the languages from the api if none exist
   *
   * @returns {Promise<void>}
   */
  static async initializeLanguages() {
    // Get the current locale
    const oldSelectedLocale = LanguagesManagerService.getSelectedLocale();

    // Clear loaded languages
    LanguagesManagerService._setLanguages([]);

    LanguagesManagerService._setLoadingLanguages(true);
    LanguagesManagerService._setLoadError(false);

    try {
      let didSetFallback = false;
      let didSetSelected = false;

      const languagesRes = await languagesApi.getAllLanguages();

      const languages = languagesRes.body;

      LanguagesManagerService._setLanguages(languages);

      const selectedLocaleIsInConfiguredLanguages = !!languages.find(
        language => language.identifier === oldSelectedLocale
      );

      // If the old selected (or initial browser language) is in the app languages, re use it
      // to clean state
      // It doesn't matter if the language messages do not load, so we can do this right away
      if (selectedLocaleIsInConfiguredLanguages) {
        LanguagesManagerService._setSelectedLocale(oldSelectedLocale);
        didSetSelected = true;
      }

      const loadAllLanguagesPromises = languages.map(language =>
        LanguagesManagerService._loadLocaleMessages(language.identifier)
          // eslint-disable-next-line promise/prefer-await-to-then
          .then(() => {
            return { didLoad: true, language };
          })
          .catch(() => {
            LanguagesManagerService._setLoadError(true);
            return { didLoad: false, language };
          })
      );

      const loadMessagesResults = await Promise.all(loadAllLanguagesPromises);
      for (const result of loadMessagesResults) {
        // Only use locales whose messages did load
        if (!result.didLoad) {
          continue;
        }

        // Use the current iteration locale as the selected locale
        // if we did not already set it
        if (!didSetSelected) {
          LanguagesManagerService._setSelectedLocale(result.language.identifier);
          didSetSelected = true;
        }

        // Use the next locale (that we did not use as the main locale),
        // to be the fallback locale
        // eslint-disable-next-line promise/always-return
        if (
          !didSetFallback &&
          didSetSelected &&
          LanguagesManagerService.getSelectedLocale() !== result.language.identifier
        ) {
          LanguagesManagerService._setFallbackLocale(result.language.identifier);
          didSetFallback = true;
        }
      }

      // If somehow, no locale was set as selected/fallback, just
      // use the first for both
      if (languages.length) {
        if (!didSetSelected) {
          LanguagesManagerService._setSelectedLocale(languages[0].identifier);
        }
        if (!didSetFallback) {
          LanguagesManagerService._setFallbackLocale(languages[0].identifier);
        }
      }
      LanguagesManagerService._setLoadingLanguages(false);
    } catch (err) {
      LanguagesManagerService._setLoadingLanguages(false);
      LanguagesManagerService._setLoadError(true);
      throw err;
    }
  }

  /**
   * Loads a locale's messages and adds them to the i18n instance
   *
   * @param {string} locale - the locale for which to load the messages
   * @returns {Promise<void>}
   * @private
   * @throws
   */
  static async _loadLocaleMessages(locale) {
    let messages;
    if (process.env.VUE_APP_LOCAL_I18N) {
      messages = await import(`../../../../src/Evolution.Piivo/i18n/${locale}.json`);
    } else {
      const res = await languagesApi.getLocaleMessages(locale);
      messages = res.body;
    }

    I18nManager.mergeLocaleMessages(locale, messages);
  }
}
